import { useInfiniteQuery, useQuery, keepPreviousData } from '@tanstack/react-query';
import {
    fetchPractices,
    fetchPracticeFilters,
    type PracticeFilterOptions,
    type PracticesListParams
} from '../services/accounts';
import { ACCOUNTS_PRACTICES_FILTERS_KEY, ACCOUNTS_PRACTICES_KEY } from './queryKeys';

const EMPTY_PRACTICES_META = {
    total: 0,
    page: 1,
    limit: 0,
    totalPages: 1,
    summary: { withOpenCalls: 0, noActivity90d: 0, addedThisMonth: 0 }
};

export function usePractices(params: PracticesListParams & { enabled?: boolean }) {
    const { enabled = true, ...rest } = params;
    const queryKey = [
        ACCOUNTS_PRACTICES_KEY,
        'list',
        rest.page,
        rest.limit,
        rest.q ?? '',
        rest.sort ?? '',
        rest.order ?? '',
        rest.hasOpenCalls ? 'open' : '',
        rest.accountType?.join(',') ?? '',
        rest.state?.join(',') ?? '',
        rest.city?.join(',') ?? '',
        rest.riskStatus?.join(',') ?? ''
    ];

    const { data, isLoading, isFetching, isError, refetch } = useQuery({
        queryKey,
        queryFn: () => fetchPractices(rest),
        staleTime: 60_000,
        placeholderData: keepPreviousData,
        enabled
    });

    return {
        practices: data?.data ?? [],
        meta: data?.meta ?? { ...EMPTY_PRACTICES_META, page: rest.page, limit: rest.limit },
        isLoading,
        isFetching,
        isError,
        refetch
    };
}

/**
 * Same endpoint, accumulated instead of sliced - `DataTable`'s `'infinite'`
 * mode. The query key omits `page` and is nested under `ACCOUNTS_PRACTICES_KEY`
 * (see `ACCOUNTS_PRACTICES_INFINITE_KEY`), so a practice mutation's
 * `invalidateQueries` can target it explicitly alongside the paged list.
 */
export function useInfinitePractices(
    params: Omit<PracticesListParams, 'page'> & { enabled?: boolean }
) {
    const { enabled = true, ...rest } = params;
    const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        isError,
        error,
        hasNextPage,
        fetchNextPage
    } = useInfiniteQuery({
        queryKey: [
            ACCOUNTS_PRACTICES_KEY,
            'infinite',
            rest.limit,
            rest.q ?? '',
            rest.sort ?? '',
            rest.order ?? '',
            rest.hasOpenCalls ? 'open' : '',
            rest.accountType?.join(',') ?? '',
            rest.state?.join(',') ?? '',
            rest.city?.join(',') ?? '',
            rest.riskStatus?.join(',') ?? ''
        ],
        initialPageParam: 1,
        queryFn: ({ pageParam }) => fetchPractices({ ...rest, page: pageParam as number }),
        getNextPageParam: (lastPage) =>
            lastPage.meta.page * lastPage.meta.limit < lastPage.meta.total
                ? lastPage.meta.page + 1
                : undefined,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
        enabled
    });

    const firstPageMeta = data?.pages[0]?.meta;

    return {
        practices: data?.pages.flatMap((page) => page.data) ?? [],
        meta: firstPageMeta ?? { ...EMPTY_PRACTICES_META, limit: rest.limit },
        isLoading,
        isFetching,
        isError,
        error,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage
    };
}

const EMPTY_PRACTICE_FILTER_OPTIONS: PracticeFilterOptions = {
    states: [],
    accountTypes: [],
    cities: []
};

export function usePracticeFilters() {
    const { data, isLoading } = useQuery({
        queryKey: [ACCOUNTS_PRACTICES_FILTERS_KEY],
        queryFn: fetchPracticeFilters,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true
    });

    return { filterOptions: data ?? EMPTY_PRACTICE_FILTER_OPTIONS, isLoading };
}
